/**
 * Crime & safety as two pictures, like Risk & quality above it: violent and
 * property crime, each a mark, the latest month's offense count, the year's
 * months as bars and the state's rate beside the national one (Edward, 30 Oct
 * 2026: "visuals graphics to represent the underlying data").
 *
 * This is the FBI's state reporting, not crime at the address, and nothing
 * here grades the property: no safety rating is inferred. A month the FBI did
 * not report is a gap, never a zero, and no annual total is summed from months.
 */
import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { KeyRound, ShieldAlert, Siren, type LucideIcon } from 'lucide-react'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import api from '@/services/api'
import { Badge } from './RiskQualityTiles'
import { DossierPanel } from './ui'

type CrimeRow = {
  series: string
  month: string
  unit: 'count' | 'per100k'
  value: number | null
}
type CrimeCategory = {
  label: string
  status: string
  detail?: string
  rows: CrimeRow[]
}
type CrimeReport = {
  status: string
  state: string
  year: number
  detail?: string
  retrievedAt?: string
  sourceUrl: string
  categories: CrimeCategory[]
}

// A feed may carry a national benchmark beside the state. It is never shown as
// the state's own count.
const NATIONAL_RE = /united states|national/i
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  'Violent crime': Siren,
  'Property crime': KeyRound,
}
const PLACEHOLDER: CrimeCategory[] = [
  { label: 'Violent crime', status: 'unavailable', rows: [] },
  { label: 'Property crime', status: 'unavailable', rows: [] },
]

/** "12-2025" as "Dec 2025". */
export function monthLabel(month: string): string {
  const [mm, yyyy] = month.split('-')
  const name = MONTHS[Number(mm) - 1]
  return name && yyyy ? `${name} ${yyyy}` : month
}

/** The one series of a unit that is the state's own, or null when there is not exactly one. */
function stateSeries(rows: CrimeRow[], unit: CrimeRow['unit']): string | null {
  const labels = [...new Set(rows.filter((r) => r.unit === unit && !NATIONAL_RE.test(r.series)).map((r) => r.series))]
  return labels.length === 1 ? labels[0] : null
}

function nationalSeries(rows: CrimeRow[], unit: CrimeRow['unit']): string | null {
  const labels = [...new Set(rows.filter((r) => r.unit === unit && NATIONAL_RE.test(r.series)).map((r) => r.series))]
  return labels.length === 1 ? labels[0] : null
}

export type CrimeSummary = {
  /** The state's count series, as the FBI names it. */
  series: string | null
  /** Twelve months of the year; null where the FBI reported nothing. */
  monthly: { month: string; value: number | null }[]
  latest: { month: string; value: number } | null
  /** The latest month both the state's and the national rate are reported. */
  rate: { month: string; state: number; national: number } | null
}

/** One category read for the tile: what the FBI reported, and nothing it did not. */
export function summarizeCrime(category: CrimeCategory, year: number): CrimeSummary {
  const series = stateSeries(category.rows, 'count')
  const valueAt = (name: string | null, unit: CrimeRow['unit'], month: string) =>
    name ? category.rows.find((r) => r.series === name && r.unit === unit && r.month === month)?.value ?? null : null
  const months = Array.from({ length: 12 }, (_, i) => `${String(i + 1).padStart(2, '0')}-${year}`)
  const monthly = series ? months.map((month) => ({ month, value: valueAt(series, 'count', month) })) : []
  const last = [...monthly].reverse().find((m) => m.value !== null)
  const stateRate = stateSeries(category.rows, 'per100k')
  const usRate = nationalSeries(category.rows, 'per100k')
  let rate: CrimeSummary['rate'] = null
  for (const month of [...months].reverse()) {
    const state = valueAt(stateRate, 'per100k', month)
    const national = valueAt(usRate, 'per100k', month)
    if (state !== null && national !== null) {
      rate = { month, state, national }
      break
    }
  }
  return { series, monthly, latest: last && last.value !== null ? { month: last.month, value: last.value } : null, rate }
}

/** The year's months as bars. A missing month is a stub on the baseline, not a zero bar. */
function MonthBars({ monthly, year, large }: { monthly: CrimeSummary['monthly']; year: number; large: boolean }) {
  const reported = monthly.filter((m) => m.value !== null)
  const max = Math.max(1, ...reported.map((m) => m.value as number))
  const label = reported.length
    ? `Monthly offenses, ${year}: ${reported.length} of 12 months reported`
    : `No monthly offense counts reported for ${year}`
  const bars = monthly.length ? monthly : Array.from({ length: 12 }, () => ({ month: '', value: null }))
  return (
    <div
      role="img"
      aria-label={label}
      title={label}
      data-testid="crime-month-bars"
      // In the dashboard slot the bars take the height left over and are the first thing to give way
      // on a short window: the count and the rates stay readable.
      className={`flex items-end gap-[2px] ${large ? 'h-16' : 'min-h-0 max-h-10 flex-1'}`}
    >
      {bars.map((m, i) => (
        <span
          key={m.month || i}
          data-reported={m.value !== null ? 'true' : 'false'}
          title={m.month ? `${monthLabel(m.month)}: ${m.value === null ? 'not reported' : m.value.toLocaleString()}` : undefined}
          className={`flex-1 rounded-sm ${m.value === null ? 'h-[2px] bg-stone-700/70' : 'bg-sky-400/70'}`}
          style={m.value === null ? undefined : { height: `${Math.max(8, (m.value / max) * 100)}%` }}
        />
      ))}
    </div>
  )
}

/** The state's rate per 100,000 beside the national one, for the same month. */
function RateBars({ rate, stateCode }: { rate: CrimeSummary['rate']; stateCode: string | null }) {
  const max = rate ? Math.max(rate.state, rate.national, 0.0001) : 1
  const rows: { key: string; label: string; value: number | null; tone: string }[] = [
    { key: 'state', label: stateCode ?? 'State', value: rate?.state ?? null, tone: 'bg-amber-300/80' },
    { key: 'us', label: 'US', value: rate?.national ?? null, tone: 'bg-stone-400/70' },
  ]
  const caption = rate ? `Per 100,000 residents, ${monthLabel(rate.month)}` : 'No state and national rate for the same month'
  return (
    <div className="flex shrink-0 flex-col gap-[3px]" role="img" aria-label={rate ? `${caption}: ${rows.map((r) => `${r.label} ${r.value}`).join(', ')}` : caption} title={caption} data-testid="crime-rate-bars">
      {rows.map((row) => (
        <div key={row.key} className="flex items-center gap-1.5" data-rate={row.key}>
          <span className="w-6 shrink-0 font-mono text-[8px] uppercase tracking-wider text-stone-500">{row.label}</span>
          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-stone-800">
            {row.value !== null ? (
              <span className={`block h-full rounded-full ${row.tone}`} style={{ width: `${Math.max(3, (row.value / max) * 100)}%` }} />
            ) : null}
          </span>
          <span className="w-9 shrink-0 text-right font-mono text-[9px] text-stone-400">
            {row.value !== null ? row.value.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
          </span>
        </div>
      ))}
    </div>
  )
}

function CrimeTile({ category, year, stateCode, large = false, children }: {
  category: CrimeCategory
  year: number
  stateCode: string | null
  large?: boolean
  children?: ReactNode
}) {
  const summary = summarizeCrime(category, year)
  const Icon = CATEGORY_ICONS[category.label] ?? ShieldAlert
  const latest = summary.latest
  const headline = latest ? latest.value.toLocaleString() : category.status === 'no_data' ? 'No reported data' : 'Not available'
  return (
    <section
      data-testid={`crime-tile-${category.label.toLowerCase().replace(/\s+/g, '-')}`}
      data-state={latest ? 'reported' : category.status}
      className={`flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md border border-white/[0.06] bg-black/30 ${large ? 'gap-2 p-2.5' : 'gap-1 p-1.5'}`}
    >
      <div className="flex min-w-0 shrink-0 items-center gap-2">
        <Badge icon={Icon} tone={latest ? 'text-sky-300 ring-sky-400/50' : 'text-stone-500'} mark={latest ? undefined : 'missing'} />
        <div className="min-w-0">
          <h3 className="truncate font-mono text-[8px] uppercase tracking-widest text-stone-500">{category.label}</h3>
          <p className={`font-mono font-semibold leading-tight ${latest ? 'text-stone-100' : 'text-stone-400'} ${large ? 'text-lg' : 'text-[13px]'}`}>
            {headline}
          </p>
          <p className="truncate text-[9px] leading-tight text-stone-500">{latest ? `${monthLabel(latest.month)} · offenses` : 'FBI state report'}</p>
        </div>
      </div>
      <MonthBars monthly={summary.monthly} year={year} large={large} />
      <RateBars rate={summary.rate} stateCode={stateCode} />
      {children}
    </section>
  )
}

export function CrimeSafetyPanel({ state }: { state?: string | null }) {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear - 1)
  const query = useQuery({
    queryKey: ['crime-intel', state, year],
    queryFn: () =>
      api
        .get<CrimeReport>('/property-intel/crime', { params: { state, year } })
        .then((r) => r.data),
    enabled: Boolean(state),
    staleTime: 3600_000,
    retry: false,
  })
  const data = query.data
  const categories = data?.categories.length ? data.categories : PLACEHOLDER
  const stateCode = state ?? null
  const yearSelect = (
    <select
      aria-label="Crime reporting year"
      value={year}
      onChange={(e) => setYear(Number(e.target.value))}
      className="rounded border border-stone-700 bg-[#111] px-1 py-0.5 text-[10px] text-stone-300"
    >
      {Array.from({ length: 10 }, (_, i) => currentYear - 1 - i).map((y) => (
        <option key={y}>{y}</option>
      ))}
    </select>
  )
  // Why the tiles are empty, in the panel's own words: the lookup is switched
  // off, the state is unknown, or the feed failed.
  const note = !state
    ? 'Resolve the property’s US state to load crime data.'
    : query.isLoading
      ? 'Loading FBI reports…'
      : query.isError
        ? 'Crime data unavailable. Retry when the service is available.'
        : data?.detail ?? null
  const headerExtra = (
    <span className="flex items-center gap-1.5">
      <span className="font-mono text-[9px] uppercase tracking-wider text-stone-400" title="FBI state reporting, not crime at this address">
        {state ? `${state} · state` : 'No state'}
      </span>
      {yearSelect}
    </span>
  )
  return (
    <DossierPanel
      title="Crime & safety"
      landscape
      dense
      scroll={false}
      headerExtra={headerExtra}
      className="h-full min-h-[170px] xl:min-h-0"
    >
      {({ expanded }) =>
        expanded ? (
          <div className="flex flex-col gap-3 text-[11px]">
            <div className="flex items-center gap-2">
              <p className="min-w-0 flex-1 text-stone-400">
                {state ? `${state} · FBI reported crime, state level` : 'State not resolved'}
              </p>
              {yearSelect}
            </div>
            {note ? <p className="text-stone-400">{note}</p> : null}
            <div className="grid gap-4 md:grid-cols-2">
              {categories.map((category) => {
                const summary = summarizeCrime(category, year)
                return (
                  <CrimeTile key={category.label} category={category} year={year} stateCode={stateCode} large>
                    <p className="text-stone-400">
                      {category.detail ??
                        'Monthly offense counts. Missing months remain gaps; no annual total is inferred.'}
                    </p>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={summary.monthly}>
                          <XAxis dataKey="month" stroke="#aaa" fontSize={10} />
                          <YAxis stroke="#aaa" fontSize={10} />
                          <Tooltip contentStyle={{ background: '#111' }} />
                          <Line dataKey="value" name="Offenses" stroke="#77c3f4" connectNulls={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <table className="w-full text-left">
                      <thead>
                        <tr>
                          <th>Series</th>
                          <th>Month</th>
                          <th>Measure</th>
                          <th className="text-right">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {category.rows.map((r) => (
                          <tr key={`${r.series}-${r.unit}-${r.month}`} className="border-t border-white/10">
                            <td className="py-1 pr-2">{r.series}</td>
                            <td>{r.month}</td>
                            <td>{r.unit === 'count' ? 'Offenses' : 'Per 100,000'}</td>
                            <td className="text-right">{r.value?.toLocaleString() ?? 'Not reported'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CrimeTile>
                )
              })}
            </div>
            <p className="text-[9px] text-stone-400">
              State reporting coverage; not crime at this address or within the
              amenity radius. No safety rating is inferred.
            </p>
            <p className="text-stone-400">
              Reporting participation and completeness vary.{' '}
              {data?.retrievedAt ? `Retrieved ${new Date(data.retrievedAt).toLocaleString()}. ` : ''}
              <a
                className="text-sky-300"
                href="https://cde.ucr.cjis.gov/LATEST/webapp/#/pages/docApi"
                target="_blank"
                rel="noopener noreferrer"
              >
                FBI source and methodology
              </a>
            </p>
          </div>
        ) : (
          <div className="flex h-full min-h-0 flex-col gap-1 text-[10px]">
            <div className="grid min-h-0 flex-1 grid-cols-2 gap-1.5">
              {categories.map((category) => (
                <CrimeTile key={category.label} category={category} year={year} stateCode={stateCode} />
              ))}
            </div>
            {note ? (
              <p className="shrink-0 truncate text-[9px] leading-tight text-stone-500" title={note} data-testid="crime-note">
                {note}
              </p>
            ) : null}
          </div>
        )
      }
    </DossierPanel>
  )
}
